import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { z } from "zod";
import { checkMessage } from "@/lib/zodMessages";
import { useLingui } from "@lingui/react/macro";
import { Trans } from "@lingui/react";
import { parseISO, isValid, addDays } from "date-fns";
import { formatDateDisplay } from "@/lib/format";
import { formatLocalDate, toOrgDateOnly } from "@/utils/dateUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { TimePicker } from "@/components/ui/time-picker";
import { BaseEditableForm } from "@/components/form/BaseEditableForm";
import type {
  EnhancedSelectOption,
  FieldConfig,
  GroupConfig,
} from "@/components/form/types";
import { useCreateSettlingIn, useCreateScheduleEntries } from "@/hooks/useSettlingIn";
import { useChildren } from "@/hooks/useChildren";
import { useGroups } from "@/hooks/useGroups";
import { filterGroupsForChildcare } from "@/utils/groupFilters";
import { useChildcares } from "@/hooks/settings/useChildcare";
import { useContracts } from "@/hooks/useContracts";
import type { TransformedContract } from "@/types/billing";
import { useAllocations } from "@/hooks/useAllocations";
import { useModuleGate } from "@/hooks/useAccess";
import { ALLOCATION_READ_MODULES, CONTRACT_READ_MODULES } from "@/lib/modules";
import { useNotAvailableForRoleLabel } from "@/components/auth/ModuleUnavailableHint";
import { useBillingSettings } from "@/hooks/useBilling";
import { useCurrency } from "@/hooks/useCurrency";
import { useClosureDates } from "@/hooks/usePublicHolidays";
import { formatMoney, minorToMajor } from "@/lib/money";
import {
  resolveBillingModeLabel,
  resolveSubsidyBillingChoiceLabel,
} from "@/lib/settlingInBillingLabels";
import { computeSettlingInEndDate } from "./settlingInEndDate";
import { findFirstAllocation } from "./findFirstAllocation";
import { buildAllocationPrefill } from "./buildAllocationPrefill";
import { buildScheduleSeedEntries } from "./buildScheduleSeedEntries";
import { useImageUrl } from "@/hooks/useImage";
import { isImageId, isDirectImageUrl } from "@/utils/imageUtils";
import { formatChildAge } from "@/utils/formatChildAge";
import { useNavigate } from "react-router";

interface CreateSettlingInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CreateFormData extends Record<string, unknown> {
  child_id: string;
  contract_id: string;
  start_date: string;
  end_date: string;
  childcare_id: string;
  group_id: string;
  comment: string;
  /**
   * The three process flags as one checklist record (S17's control, reused).
   * Unpacked back to `date_agreed` / `info_sent` / `info_confirmed` at submit,
   * so the persisted field names and the payload shape are unchanged.
   */
  process_flags: Record<string, boolean>;
}

interface ChildData {
  id?: string;
  firstname?: string;
  lastname?: string;
  avatarUrl?: string;
  childInternalId?: string;
  birthdate?: string;
}

/** The staged flow: Who → Billing snapshot → optional Seed schedule. */
const STEP_WHO = 0;
const STEP_BILLING = 1;
const STEP_SEED = 2;
const TOTAL_STEPS = 3;

/** The fields the allocation pre-fill may write; a hand edit to any of them wins (D3). */
const PREFILL_FIELDS = ["contract_id", "start_date", "end_date", "childcare_id", "group_id"] as const;

function ChildAvatar({ child }: { child: ChildData }) {
  const avatarUrlRaw = child.avatarUrl;
  const avatarUrl = avatarUrlRaw?.trim() || undefined;

  const needsImageResolution = isImageId(avatarUrl);
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined;
  }

  const first = child.firstname?.charAt(0) || "";
  const last = child.lastname?.charAt(0) || "";
  const initials = first && last ? `${first}${last}`.toUpperCase() : first.toUpperCase() || "??";

  return (
    <Avatar className="h-8 w-8 flex-shrink-0">
      {finalAvatarUrl && (
        <AvatarImage
          src={finalAvatarUrl}
          alt={`${child.firstname || ""} ${child.lastname || ""}`.trim() || "Child"}
        />
      )}
      <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

function emptyValues(): CreateFormData {
  return {
    child_id: "",
    contract_id: "",
    start_date: "",
    end_date: "",
    childcare_id: "",
    group_id: "",
    comment: "",
    process_flags: {},
  };
}

/**
 * Staged settling-in creation (ST-5/S16 part 2 + S21).
 *
 * Runs entirely on `BaseEditableForm` — the raw RHF `<form>` and its eight
 * `FormField`s are gone — and presents the sequentially-dependent choices as
 * three short steps instead of one tall flat form:
 *
 *   1. Who      — child → contract → auto-derived dates
 *   2. Billing  — the org default snapshot, READ-ONLY, with an Adjust affordance
 *   3. Seed     — optional schedule generation, skippable
 *
 * ONE form instance stays mounted across the steps (only the rendered group
 * changes), so values entered on step 1 survive to submit. The staged controls
 * are passed as `children`, which render inside the shared `<form>` — so the
 * finishing buttons are real `type="submit"` buttons going through the shared
 * form's validation and submit handler, not a programmatic click.
 */
export function CreateSettlingInDialog({ open, onOpenChange }: CreateSettlingInDialogProps) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const createMutation = useCreateSettlingIn();
  // Silent: seeding days is part of the create; the create toast reports it.
  const createScheduleMutation = useCreateScheduleEntries({ silent: true });

  const [step, setStep] = useState(STEP_WHO);
  /**
   * Bumped on every open. `BaseEditableForm` seeds RHF from `defaultValues` at
   * MOUNT ONLY, so without a changing `key` a second open would still carry the
   * previous session's values.
   */
  const [openNonce, setOpenNonce] = useState(0);
  const [childSearchTerm, setChildSearchTerm] = useState("");
  /** Progressive disclosure on step 2: read-only until the operator asks to adjust. */
  const [showAdjust, setShowAdjust] = useState(false);

  // Seed-step controls (step 3). Local: they are not part of the plan payload.
  const [seedDays, setSeedDays] = useState(10);
  const [seedChildFrom, setSeedChildFrom] = useState("09:00");
  const [seedChildTo, setSeedChildTo] = useState("11:00");
  const [seedContactFrom, setSeedContactFrom] = useState("09:00");
  const [seedContactTo, setSeedContactTo] = useState("11:00");

  /**
   * A mirror of the form's values, fed by `onFieldChange`. The staged flow reads
   * values OUTSIDE a change event — to gate Next and to drive dependent option
   * lists — which the shared form does not otherwise expose.
   */
  const [values, setValues] = useState<CreateFormData>(() => emptyValues());
  /**
   * The shared form's `setValue`, captured on the first field change. The
   * allocation pre-fill effect runs on query arrival rather than inside a change
   * event, so it needs a handle to the setter.
   */
  const setValueRef = useRef<
    ((name: never, value: unknown, options?: { shouldDirty?: boolean }) => void) | null
  >(null);
  /**
   * Allocation pre-fill state (D3): the child it already ran for, the pre-fill
   * fields edited by hand since that child was chosen, whether the start came from
   * the pre-fill, and the group it wrote (drives the hint and the contract rule).
   */
  const prefilledChildRef = useRef<string | null>(null);
  const manualEditsRef = useRef<Set<string>>(new Set());
  const startFromAllocationRef = useRef(false);
  const [prefilledGroupId, setPrefilledGroupId] = useState<string | null>(null);
  /**
   * Whether the seed step should run on submit. A ref, not state: the two
   * finishing buttons both submit the same form and the flag must be readable
   * synchronously inside that submit.
   */
  const seedRequestedRef = useRef(false);

  // Reset the whole session when the dialog opens.
  useEffect(() => {
    if (open) {
      setStep(STEP_WHO);
      setShowAdjust(false);
      setChildSearchTerm("");
      setValues(emptyValues());
      setOpenNonce((n) => n + 1);
      prefilledChildRef.current = null;
      manualEditsRef.current = new Set();
      startFromAllocationRef.current = false;
      setPrefilledGroupId(null);
      seedRequestedRef.current = false;
    }
  }, [open]);

  // Fetch data
  const { data: childrenData } = useChildren(50, 0, {
    withContracts: true,
    search: childSearchTerm || undefined,
  });
  const { data: groupsData, status: groupsStatus } = useGroups(200, 0);
  const { data: childcaresData } = useChildcares({ limit: 200 });
  // The contract picker reads `GET /contracts`, which the server admits for
  // contracts-tariffs, billing or capacity only (#1691). Without them the picker is
  // disabled with the shared hint; the allocation lookup below only prefills the
  // form and is skipped silently by its own hook gate.
  const canReadContracts = useModuleGate(CONTRACT_READ_MODULES);
  const canReadAllocations = useModuleGate(ALLOCATION_READ_MODULES);
  const notAvailableForRole = useNotAvailableForRoleLabel();
  const { data: contractsData, status: contractsStatus } = useContracts(undefined, values.child_id || undefined);
  // Child-keyed and contract-independent; `from_date = today` narrows the server
  // result to allocations that have not ended, so the page cannot hide the first one.
  const orgToday = toOrgDateOnly(new Date().toISOString());
  const { data: allocationsData, status: allocationsStatus } = useAllocations(
    values.child_id || undefined, undefined, undefined, 200, 0, orgToday, undefined,
    { enabled: Boolean(values.child_id) },
  );
  // A failed billing query must never render as a blank-but-valid snapshot: the
  // step exists to show the operator what the plan will inherit, so an unknown
  // configuration has to look unknown and block the flow.
  const { data: billingSettings, status: billingStatus } = useBillingSettings();
  const billingReady = billingStatus === "success";
  const { currency } = useCurrency();
  // Same shape the Days tab reads: closure dates gate seeding, because without
  // them the generator would happily plan days on holidays and closure days.
  const { data: holidays, status: closureStatus } = useClosureDates();
  const closuresReady = closureStatus === "success" && holidays !== undefined;
  const holidaySet = useMemo(
    () => new Set((holidays ?? []).map((h) => h.holidayDate).filter(Boolean) as string[]),
    [holidays],
  );


  const children = useMemo(() => (Array.isArray(childrenData) ? childrenData : []), [childrenData]);

  const childMap = useMemo(() => {
    const map = new Map<string, ChildData>();
    children.forEach((child) => {
      if (child.id) {
        map.set(child.id, {
          id: child.id,
          firstname: child.firstname,
          lastname: child.lastname,
          avatarUrl: child.avatarUrl,
          childInternalId: child.childInternalId,
          birthdate: child.birthdate,
        });
      }
    });
    return map;
  }, [children]);

  const childOptions: EnhancedSelectOption[] = useMemo(
    () =>
      children
        .filter((child) => child.id)
        .map((child) => {
          const name = `${child.firstname || ""} ${child.lastname || ""}`.trim() || "Unknown";
          const parts = [name, child.childInternalId, formatChildAge(child.birthdate, i18n)].filter(
            Boolean,
          );
          return { value: child.id!, label: parts.join(" ") };
        }),
    [children, i18n],
  );

  /**
   * Server-side child search, surfaced through the `asyncselect` field — the
   * same uncapped `useChildren(50, 0, { withContracts, search })` path as before.
   * The shared field owns the debounce.
   *
   * NOTE the two-step handshake: this cannot await its own results, because the
   * query lives in `useChildren` (a hook, not a callable). It pushes the term
   * into state and returns the CURRENT `childOptions` — which for the first call
   * after a keystroke is still the pre-query list. When the refetch resolves,
   * `childOptions` changes, this callback's identity changes with it, and
   * AsyncSearchableSelectField re-invokes it and renders the fresh list. So the
   * stale frame is transient and self-correcting, at the cost of one extra
   * render. Do not "simplify" the `[childOptions]` dependency away — that
   * dependency IS the refresh mechanism.
   */
  const loadChildOptions = useCallback(
    async (query: string): Promise<EnhancedSelectOption[]> => {
      setChildSearchTerm(query);
      return childOptions;
    },
    [childOptions],
  );

  const renderChildOption = useCallback(
    (option: EnhancedSelectOption) => {
      const child = childMap.get(String(option.value));
      if (!child) return <span>{option.label}</span>;
      const age = formatChildAge(child.birthdate, i18n);
      return (
        <div className="flex items-center gap-3">
          <ChildAvatar child={child} />
          <span className="font-medium truncate">
            {child.firstname} {child.lastname}
            {child.childInternalId && (
              <span className="text-muted-foreground font-normal"> ({child.childInternalId})</span>
            )}
            {age && <span className="text-muted-foreground font-normal"> &bull; {age}</span>}
          </span>
        </div>
      );
    },
    [childMap, i18n],
  );

  const childcareOptions = useMemo(() => {
    const list = Array.isArray(childcaresData)
      ? childcaresData
      : (childcaresData as { data?: { id?: string; name?: string }[]; childcares?: { id?: string; name?: string }[] })?.data ||
        (childcaresData as { childcares?: { id?: string; name?: string }[] })?.childcares || [];
    return (list as { id?: string; name?: string }[]).map((cc) => ({
      value: cc.id || "",
      label: cc.name || "",
    }));
  }, [childcaresData]);

  const groupsList = useMemo(() => (Array.isArray(groupsData) ? groupsData : []), [groupsData]);
  const groupOptions = useMemo(
    () => filterGroupsForChildcare(groupsList, values.childcare_id).map((g) => ({ value: g.id || "", label: g.name || "" })),
    [groupsList, values.childcare_id],
  );

  const contracts = useMemo<TransformedContract[]>(
    () => contractsData?.contracts ?? [],
    [contractsData],
  );

  const contractOptions = useMemo(() => {
    return contracts
      .filter((contract) => {
        const record = contract as unknown as Record<string, unknown>;
        const status = ((record.status as string) || "").toUpperCase();
        if (!status) return true;
        return status === "ACTIVE" || status === "UPCOMING";
      })
      .map((contract) => {
        const record = contract as unknown as Record<string, unknown>;
        const contractNumber = contract.id ? contract.id.substring(0, 8).toUpperCase() : "";
        const version = record.versionNumber ? `V${record.versionNumber}` : "";
        const status = ((record.status as string) || "").toUpperCase();

        const startDate = (record.activeFrom as string) || (record.startDate as string);
        const endDate = (record.activeTo as string) || (record.endDate as string);
        const formattedStart = startDate ? formatDateDisplay(startDate) : "";
        const formattedEnd = endDate ? formatDateDisplay(endDate) : "";

        const parts = [contractNumber, version, formattedStart, formattedEnd].filter(Boolean);
        let label = parts.length > 0 ? parts.join(" - ") : contractNumber || i18n._({ id: "contracts.unknownContract", message: "Unknown contract" });

        if (status === "ACTIVE") {
          label += ` (${i18n._({ id: "contract.status.active", message: "Active" })})`;
        } else if (status === "UPCOMING") {
          label += ` (${i18n._({ id: "contract.status.upcoming", message: "Upcoming" })})`;
        }

        return { value: contract.id || "", label };
      });
  }, [contracts, i18n]);

  /**
   * Derive start/end from a contract — the pre-migration logic, unchanged:
   * start = the first weekday from the contract start that has a service in the
   * pattern; end = the 10th business day inclusive.
   */
  const deriveContractDates = useCallback(
    (contractId: string): { startDate: string; endDate: string } | null => {
      const contract = contracts.find((c) => c.id === contractId);
      if (!contract) return null;

      const record = contract as unknown as Record<string, unknown>;

      // ContractTransformer converts startDate to a Date; activeFrom stays a string.
      let contractStart: Date | undefined;
      const startDateVal = record.startDate;
      const activeFromVal = record.activeFrom;

      if (startDateVal instanceof Date && isValid(startDateVal)) {
        contractStart = startDateVal;
      } else if (typeof startDateVal === "string" && startDateVal) {
        const parsed = parseISO(startDateVal);
        if (isValid(parsed)) contractStart = parsed;
      }

      if (!contractStart && typeof activeFromVal === "string" && activeFromVal) {
        const parsed = parseISO(activeFromVal);
        if (isValid(parsed)) contractStart = parsed;
      }

      if (!contractStart) return null;

      const servicePattern = record.servicePattern as Record<string, unknown[]> | undefined;
      const weekdayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

      let startDate = contractStart;
      if (servicePattern) {
        for (let i = 0; i < 7; i++) {
          const candidate = addDays(contractStart, i);
          const dayName = weekdayNames[candidate.getDay()];
          const services = servicePattern[dayName];
          if (Array.isArray(services) && services.length > 0) {
            startDate = candidate;
            break;
          }
        }
      }

      return {
        startDate: formatLocalDate(startDate),
        endDate: formatLocalDate(computeSettlingInEndDate(startDate)),
      };
    },
    [contracts],
  );

  /**
   * The cascade on the shared form's `onFieldChange`, which fires for USER edits
   * only -- so every pre-fill field seen here is a hand edit (D3).
   */
  const handleFieldChange = useCallback(
    (
      name: string,
      value: unknown,
      setValue: (name: never, value: unknown, options?: { shouldDirty?: boolean }) => void,
    ) => {
      setValueRef.current = setValue;
      const next = String(value ?? "");
      if ((PREFILL_FIELDS as readonly string[]).includes(name)) manualEditsRef.current.add(name);

      if (name === "child_id") {
        // Clears the contract, both dates and the placement; the pre-fill re-runs.
        PREFILL_FIELDS.forEach((field) => setValue(field as never, ""));
        prefilledChildRef.current = null;
        manualEditsRef.current = new Set();
        startFromAllocationRef.current = false;
        setPrefilledGroupId(null);
        setValues((prev) => ({ ...prev, child_id: next, contract_id: "", start_date: "", end_date: "", childcare_id: "", group_id: "" }));
        return;
      }

      if (name === "contract_id") {
        // Only a contract-derived start follows the contract, and only a placement
        // NOT taken from the allocation is dropped (today's behaviour otherwise).
        const rederiveDates = !startFromAllocationRef.current && !manualEditsRef.current.has("start_date");
        const clearPlacement = prefilledGroupId === null;
        if (clearPlacement) {
          setValue("childcare_id" as never, "");
          setValue("group_id" as never, "");
        }
        // D2 fallback, late: an org-wide group from the allocation found no childcare
        // while no contract was chosen; the contract picked now supplies it.
        const fallbackChildcareId =
          !clearPlacement && !values.childcare_id
            ? contracts.find((c) => c.id === next)?.childcareId
            : undefined;
        if (fallbackChildcareId) setValue("childcare_id" as never, fallbackChildcareId);
        const derived = rederiveDates ? deriveContractDates(next) : null;
        if (derived) {
          setValue("start_date" as never, derived.startDate);
          setValue("end_date" as never, derived.endDate);
        }
        setValues((prev) => ({
          ...prev,
          contract_id: next,
          ...(clearPlacement ? { childcare_id: "", group_id: "" } : {}),
          ...(fallbackChildcareId ? { childcare_id: fallbackChildcareId } : {}),
          ...(rederiveDates
            ? { start_date: derived?.startDate ?? "", end_date: derived?.endDate ?? "" }
            : {}),
        }));
        return;
      }

      if (name === "start_date") startFromAllocationRef.current = false;
      // A hand-picked placement is no longer "from the allocation".
      if (name === "childcare_id" || name === "group_id") setPrefilledGroupId(null);

      if (name === "childcare_id") {
        setValue("group_id" as never, "");
        setValues((prev) => ({ ...prev, childcare_id: next, group_id: "" }));
        return;
      }

      if (name === "process_flags") {
        setValues((prev) => ({
          ...prev,
          process_flags: (value ?? {}) as Record<string, boolean>,
        }));
        return;
      }

      // A `type: "date"` field hands back a `Date`; the schema normalizes it only at
      // SUBMIT, but this mirror is read long before then, so normalize HERE.
      setValues(
        (prev) =>
          ({
            ...prev,
            [name]: value instanceof Date ? formatLocalDate(value) : (value ?? ""),
          }) as CreateFormData,
      );
    },
    [deriveContractDates, prefilledGroupId, values.childcare_id, contracts],
  );

  /**
   * Pre-fill from the child's first allocation (#1685 D1-D3), ONCE per chosen child,
   * never overwriting a field edited by hand. The child is marked handled BEFORE any
   * early return, so a late refetch can never re-run it and clobber a later manual
   * edit (R3). An allocations error (a 403 included) skips the allocation pre-fill
   * silently (D6).
   *
   * Then the single-contract rule (#1697): a child with exactly one contract option
   * gets it pre-selected, with the dates derived exactly as a manual pick derives
   * them. It also runs when the allocation pre-fill had nothing to offer (gate
   * closed, errored, no allocation), but the allocation pre-fill keeps priority, and
   * a value picked by hand is never overwritten. Applied through the form API, not
   * `defaultValues`: BaseEditableForm reads those only at mount, and the child is
   * chosen after mount.
   */
  useEffect(() => {
    const childId = values.child_id;
    if (!childId || prefilledChildRef.current === childId) return;
    // A module-gated query never leaves "pending", so a closed gate counts as settled.
    const waitingOn = [
      canReadAllocations ? allocationsStatus : "success",
      canReadContracts ? contractsStatus : "success",
      groupsStatus,
    ];
    if (waitingOn.includes("pending")) return;
    prefilledChildRef.current = childId;

    const edited = manualEditsRef.current;
    const patch: Partial<CreateFormData> = {};

    const allocationsUsable = canReadAllocations && allocationsStatus !== "error";
    const first = allocationsUsable ? findFirstAllocation(allocationsData?.allocations, orgToday) : undefined;
    const prefill = first
      ? buildAllocationPrefill({
          first,
          contractOptionIds: contractOptions.map((o) => o.value),
          contracts,
          groups: groupsList,
          currentContractId: values.contract_id,
        })
      : null;

    if (prefill) {
      if (prefill.contractId && !edited.has("contract_id")) patch.contract_id = prefill.contractId;
      if (!edited.has("start_date")) {
        patch.start_date = prefill.startDate;
        startFromAllocationRef.current = true;
        if (!edited.has("end_date")) patch.end_date = prefill.endDate;
      }
      // A hand-picked childcare that does not offer the allocation's group wins over the
      // group too: the Select would hold a value it cannot show, and submit it unseen.
      const groupOffered =
        !edited.has("childcare_id") || groupOptions.some((o) => o.value === prefill.groupId);
      if (prefill.groupId && groupOffered && !edited.has("group_id")) patch.group_id = prefill.groupId;
      // The mirror: a hand-picked group the allocation's childcare does not offer wins over
      // the childcare too, or the group Select would lose the value it holds.
      const handGroup = edited.has("group_id") ? groupsList.find((g) => g.id === values.group_id) : undefined;
      const childcareFits =
        !handGroup || filterGroupsForChildcare([handGroup], prefill.childcareId).length > 0;
      if (prefill.childcareId && childcareFits && !edited.has("childcare_id")) patch.childcare_id = prefill.childcareId;
    }

    // Single-contract rule. The start stays contract-derived (startFromAllocationRef
    // untouched), so a later manual contract change still re-derives it.
    if (
      !patch.contract_id &&
      !values.contract_id &&
      !edited.has("contract_id") &&
      contractOptions.length === 1
    ) {
      const contractId = contractOptions[0].value;
      patch.contract_id = contractId;
      if (!edited.has("start_date") && patch.start_date === undefined) {
        const derived = deriveContractDates(contractId);
        if (derived) {
          patch.start_date = derived.startDate;
          if (!edited.has("end_date")) patch.end_date = derived.endDate;
        }
      }
      // D2 fallback, as on a manual pick: an org-wide group from the allocation
      // found no childcare; the pre-selected contract supplies it.
      if (patch.group_id && !patch.childcare_id && !values.childcare_id && !edited.has("childcare_id")) {
        const fallbackChildcareId = contracts.find((c) => c.id === contractId)?.childcareId;
        if (fallbackChildcareId) patch.childcare_id = fallbackChildcareId;
      }
    }

    if (Object.keys(patch).length === 0) return;
    Object.entries(patch).forEach(([name, value]) => setValueRef.current?.(name as never, value));
    setValues((prev) => ({ ...prev, ...patch }));
    if (patch.group_id) setPrefilledGroupId(patch.group_id);
  }, [allocationsData, allocationsStatus, canReadAllocations, canReadContracts, contractsStatus, groupsStatus, contractOptions, contracts, deriveContractDates, groupsList, groupOptions, orgToday, values.child_id, values.childcare_id, values.contract_id, values.group_id]);

  const schema = useMemo(
    () =>
      z.object({
        child_id: z.string().min(1, t({ id: "settlingIn.create.validation.childRequired", message: "Child is required" })),
        contract_id: z.string().min(1, t({ id: "settlingIn.create.validation.contractRequired", message: "Contract is required" })),
        start_date: z
          .union([z.string(), z.date()])
          .transform((v) => (v instanceof Date ? formatLocalDate(v) : v))
          .pipe(
            z.string().min(1, t({ id: "settlingIn.create.validation.startDateRequired", message: "Start date is required" })),
          ),
        end_date: z
          .union([z.string(), z.date()])
          .optional()
          .transform((v) => (v instanceof Date ? formatLocalDate(v) : (v ?? ""))),
        childcare_id: z.string().min(1, t({ id: "settlingIn.create.validation.childcareRequired", message: "Childcare is required" })),
        group_id: z.string().min(1, t({ id: "settlingIn.create.validation.groupRequired", message: "Group is required" })),
        comment: z.string().max(10000, checkMessage.maxLength(i18n)).optional(),
        process_flags: z.record(z.string(), z.boolean()).optional().default({}),
        // `z.record(z.string(), z.boolean())` widens process_flags' index signature, so the
        // inferred output no longer matches CreateFormData exactly. The runtime
        // shape is identical; one cast at the schema boundary keeps the form typed.
      }) as unknown as z.ZodType<CreateFormData, CreateFormData>,
    [t, i18n],
  );

  const fields = useMemo<FieldConfig<CreateFormData>[]>(
    () => [
      {
        name: "child_id",
        type: "asyncselect",
        label: t({ id: "settlingIn.fields.child", message: "Child" }),
        required: true,
        loadOptions: loadChildOptions,
        initialOptions: childOptions,
        renderOption: renderChildOption,
        placeholder: t({ id: "settlingIn.create.selectChild", message: "Search by name or ID..." }),
        colSpan: 2,
      },
      {
        name: "contract_id",
        type: "select",
        label: t({ id: "settlingIn.fields.contract", message: "Contract" }),
        required: true,
        selectOptions: contractOptions,
        // Sequentially dependent: no contract before a child.
        disabled: !values.child_id || !canReadContracts,
        description: canReadContracts ? undefined : notAvailableForRole,
        colSpan: 2,
      },
      {
        name: "start_date",
        type: "date",
        label: t({ id: "settlingIn.fields.startDate", message: "Start Date" }),
        required: true,
      },
      {
        name: "end_date",
        type: "date",
        label: t({ id: "settlingIn.fields.endDate", message: "End Date" }),
      },
      {
        name: "childcare_id",
        type: "select",
        label: t({ id: "settlingIn.fields.childcare", message: "Childcare" }),
        required: true,
        selectOptions: childcareOptions,
      },
      {
        name: "group_id",
        type: "select",
        label: t({ id: "settlingIn.fields.group", message: "Group" }),
        required: true,
        selectOptions: groupOptions,
        description:
          prefilledGroupId && values.group_id === prefilledGroupId
            ? t({ id: "settlingIn.create.prefilledFromAllocation", message: "Taken from the child's group allocation" })
            : undefined,
      },
      {
        name: "process_flags",
        type: "checklist",
        label: t({ id: "settlingIn.fields.processFlags", message: "Process steps" }),
        colSpan: 2,
        checklistOptions: [
          { value: "date_agreed", label: t({ id: "settlingIn.fields.dateAgreed", message: "Date Agreed" }), description: t({ id: "settlingIn.fields.dateAgreedDesc", message: "Parents confirmed start date" }) },
          { value: "info_sent", label: t({ id: "settlingIn.fields.infoSent", message: "Info Sent" }), description: t({ id: "settlingIn.fields.infoSentDesc", message: "Onboarding docs sent to parents" }) },
          { value: "info_confirmed", label: t({ id: "settlingIn.fields.infoConfirmed", message: "Info Confirmed" }), description: t({ id: "settlingIn.fields.infoConfirmedDesc", message: "Parents returned completed forms" }) },
        ],
      },
      {
        name: "comment",
        type: "textarea",
        label: t({ id: "settlingIn.fields.comment", message: "Comment" }),
        colSpan: 2,
      },
    ],
    [t, loadChildOptions, childOptions, renderChildOption, contractOptions, values.child_id, values.group_id, childcareOptions, groupOptions, prefilledGroupId, canReadContracts, notAvailableForRole],
  );

  /**
   * Only the CURRENT step's group renders — that is what makes this a staged
   * flow rather than one tall form. The single form instance stays mounted, so
   * values entered on an earlier step survive to submit.
   */
  const groups = useMemo<GroupConfig<CreateFormData>[]>(() => {
    if (step === STEP_WHO) {
      return [
        {
          title: t({ id: "settlingIn.create.step.who", message: "Who" }),
          fields: ["child_id", "contract_id", "start_date", "end_date"],
        },
      ];
    }
    if (step === STEP_BILLING) {
      /**
       * The invariant: a REQUIRED field is never disclosure-gated. Childcare and
       * group gate Next, so they render always — hiding them behind Adjust made
       * the step a dead-end whenever the child has no live allocation (the
       * user saw a read-only panel and a permanently disabled Next).
       *
       * Process steps also render always. They were behind Adjust while they
       * were silently dropped on create; now that they persist, an operator who
       * has already agreed the date or sent the docs must be able to record that
       * without discovering a disclosure toggle.
       *
       * Adjust reveals only the remaining OPTIONAL plan detail. The org billing
       * snapshot stays read-only throughout: the create payload carries no
       * billing fields.
       */
      const placement: GroupConfig<CreateFormData> = {
        title: t({ id: "settlingIn.create.step.placement", message: "Placement" }),
        fields: ["childcare_id", "group_id"],
      };
      const processSteps: GroupConfig<CreateFormData> = {
        title: t({ id: "settlingIn.create.step.processSteps", message: "Process steps" }),
        fields: ["process_flags"],
      };
      if (!showAdjust) return [placement, processSteps];
      return [
        placement,
        processSteps,
        {
          title: t({ id: "settlingIn.create.billing.adjustTitle", message: "Adjust plan details" }),
          fields: ["comment"],
        },
      ];
    }
    return [];
  }, [step, showAdjust, t]);

  const defaultValues = useMemo<Partial<CreateFormData>>(() => emptyValues(), []);

  const settlingInBilling = (
    billingSettings as {
      settling_in_billing?: {
        billing_mode?: string;
        billing_amount?: number;
        subsidy_billing_choice?: string;
      };
    } | undefined
  )?.settling_in_billing;

  // The snapshot shows backend enums; they are never rendered raw.
  const modeLabel = useMemo(
    () => resolveBillingModeLabel(i18n, settlingInBilling?.billing_mode),
    [i18n, settlingInBilling?.billing_mode],
  );
  const subsidyChoiceLabel = useMemo(
    () => resolveSubsidyBillingChoiceLabel(i18n, settlingInBilling?.subsidy_billing_choice),
    [i18n, settlingInBilling?.subsidy_billing_choice],
  );

  /** Step 1 is complete once the required Who fields are filled. */
  const canLeaveWhoStep = Boolean(values.child_id && values.contract_id && values.start_date);
  /** The two required placement fields, independent of billing readiness. */
  const placementChosen = Boolean(values.childcare_id && values.group_id);
  /**
   * Step 2 is complete once the allocation-derived (or adjusted) fields exist
   * AND the billing snapshot is actually known. `success` is the only status in
   * which it is — a disabled or failed query also leaves `data` undefined.
   */
  const canLeaveBillingStep = placementChosen && billingReady;

  const handleSubmit = useCallback(
    async (submitted: CreateFormData) => {
      const flags = submitted.process_flags ?? {};
      const result = await createMutation.mutateAsync({
        child_id: submitted.child_id,
        contract_id: submitted.contract_id,
        start_date: submitted.start_date,
        end_date: submitted.end_date || undefined,
        group_id: submitted.group_id,
        childcare_id: submitted.childcare_id,
        comment: submitted.comment || undefined,
        // The persisted flag names are unchanged; the checklist is presentation.
        date_agreed: flags.date_agreed ?? false,
        info_sent: flags.info_sent ?? false,
        info_confirmed: flags.info_confirmed ?? false,
      });

      // Optional seed, using the SAME generator as the Days tab's "Generate days".
      // A plan created WITHOUT seeding must still save, so this runs strictly
      // after a successful create and never gates it.
      if (result?.id && seedRequestedRef.current && submitted.start_date) {
        const entries = buildScheduleSeedEntries({
          start: parseISO(submitted.start_date),
          days: seedDays,
          holidays: holidaySet,
          childFrom: seedChildFrom,
          childTo: seedChildTo,
          contactFrom: seedContactFrom,
          contactTo: seedContactTo,
        });
        if (entries.length > 0) {
          try {
            await createScheduleMutation.mutateAsync({ planId: result.id, data: { entries } });
          } catch {
            // The plan already exists. Letting this reject would leave the dialog
            // open on a submitted form, and the operator's retry would create a
            // SECOND plan. The mutation surfaces its own error toast; the user
            // re-seeds from the plan's Days tab.
          }
        }
      }

      // Close only inside the success path.
      onOpenChange(false);
      if (result?.id) {
        navigate(`/settling-in/${result.id}`);
      }
    },
    [
      createMutation,
      createScheduleMutation,
      holidaySet,
      seedDays,
      seedChildFrom,
      seedChildTo,
      seedContactFrom,
      seedContactTo,
      navigate,
      onOpenChange,
    ],
  );

  const isBusy = createMutation.isPending || createScheduleMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>
            <Trans id="settlingIn.create.title">Create Settling-In Plan</Trans>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans id="common.dialog.formDescription" message="Fill in the fields, then save." />
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator. Wrap-tolerant, no fixed widths — German runs long. */}
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span data-testid="create-step-indicator">
            {i18n._({
              id: "settlingIn.create.step.indicator",
              message: "Step {current} of {total}",
              values: { current: step + 1, total: TOTAL_STEPS },
            })}
          </span>
          <span className="font-medium text-foreground">
            {step === STEP_WHO && t({ id: "settlingIn.create.step.who", message: "Who" })}
            {step === STEP_BILLING && t({ id: "settlingIn.create.step.billing", message: "Billing" })}
            {step === STEP_SEED && t({ id: "settlingIn.create.step.seed", message: "Seed schedule" })}
          </span>
        </div>

        <BaseEditableForm<CreateFormData>
          key={`settling-in-create-${openNonce}`}
          schema={schema}
          fields={fields}
          groups={groups}
          defaultValues={defaultValues}
          mode="create"
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          onFieldChange={handleFieldChange}
          isSubmitting={isBusy}
          hideDefaultActions
          inSheet
        >
          {/*
            Rendered INSIDE the shared <form>, so the finishing buttons are real
            submit buttons going through its validation and submit handler.
          */}
          {step === STEP_BILLING && billingStatus === "error" && (
            <div
              className="rounded-md border border-destructive/50 bg-card p-3 text-sm text-destructive"
              role="alert"
              data-testid="billing-error"
            >
              {t({
                id: "settlingIn.create.billing.loadError",
                message:
                  "The settling-in billing configuration could not be loaded. Reload before creating the plan.",
              })}
            </div>
          )}

          {step === STEP_BILLING && billingStatus !== "error" && !billingReady && (
            <div
              className="rounded-md border bg-card p-3 text-sm text-muted-foreground"
              data-testid="billing-loading"
            >
              {t({
                id: "settlingIn.create.billing.loading",
                message: "Loading the billing configuration…",
              })}
            </div>
          )}

          {step === STEP_BILLING && billingReady && (
            <div className="rounded-md border bg-card p-3 text-sm space-y-2" data-testid="billing-snapshot">
              <p className="text-muted-foreground">
                {t({
                  id: "settlingIn.create.billing.readOnlyHint",
                  message:
                    "This plan snapshots the organisation's settling-in billing configuration at creation.",
                })}
              </p>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-muted-foreground">
                  {t({ id: "settlingIn.billingPreview.mode", message: "Mode" })}
                </span>
                <span>{modeLabel ?? "—"}</span>
              </div>
              {typeof settlingInBilling?.billing_amount === "number" && (
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-muted-foreground">
                    {t({ id: "settlingIn.billingPreview.amount", message: "Amount" })}
                  </span>
                  <span>
                    {formatMoney(minorToMajor(settlingInBilling.billing_amount), true, currency)}
                  </span>
                </div>
              )}
              {settlingInBilling?.subsidy_billing_choice && (
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-muted-foreground">
                    {t({ id: "settlingIn.create.billing.subsidyChoice", message: "Subsidy handling" })}
                  </span>
                  <span>{subsidyChoiceLabel}</span>
                </div>
              )}

              {!showAdjust && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdjust(true)}
                  data-testid="billing-adjust"
                >
                  {t({ id: "settlingIn.create.billing.adjust", message: "Adjust" })}
                </Button>
              )}
            </div>
          )}

          {step === STEP_SEED && (
            <div className="rounded-md border bg-card p-3 space-y-3" data-testid="seed-step">
              <p className="text-sm text-muted-foreground">
                {t({
                  id: "settlingIn.create.seed.description",
                  message:
                    "Optionally generate the first weekdays of the schedule now. Weekends and closure days are skipped.",
                })}
              </p>
              <div>
                <Label htmlFor="seed-days">
                  {t({ id: "settlingIn.days.generate.numberOfDays", message: "Number of weekdays" })}
                </Label>
                <Input
                  id="seed-days"
                  type="number"
                  min={1}
                  max={30}
                  value={seedDays}
                  onChange={(e) => setSeedDays(Math.min(30, Math.max(1, Number(e.target.value))))}
                />
              </div>
              {/*
                Each TimePicker is TWO inputs that already carry their own
                "Hours"/"Minutes" aria-labels — and an aria-label WINS over a
                <label htmlFor>, so htmlFor alone would leave both inputs named
                "Hours"/"Minutes" with no hint of which time they belong to.
                Naming the surrounding role="group" is what makes the field's
                purpose reachable; htmlFor stays for click-to-focus.
              */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div role="group" aria-labelledby="seed-child-from-label">
                  <Label id="seed-child-from-label" htmlFor="seed-child-from">
                    {t({ id: "settlingIn.days.generate.childFrom", message: "Child from" })}
                  </Label>
                  <TimePicker id="seed-child-from" value={seedChildFrom} onChange={setSeedChildFrom} />
                </div>
                <div role="group" aria-labelledby="seed-child-to-label">
                  <Label id="seed-child-to-label" htmlFor="seed-child-to">
                    {t({ id: "settlingIn.days.generate.childTo", message: "Child to" })}
                  </Label>
                  <TimePicker id="seed-child-to" value={seedChildTo} onChange={setSeedChildTo} />
                </div>
                <div role="group" aria-labelledby="seed-contact-from-label">
                  <Label id="seed-contact-from-label" htmlFor="seed-contact-from">
                    {t({ id: "settlingIn.days.generate.contactFrom", message: "Contact from" })}
                  </Label>
                  <TimePicker
                    id="seed-contact-from"
                    value={seedContactFrom}
                    onChange={setSeedContactFrom}
                  />
                </div>
                <div role="group" aria-labelledby="seed-contact-to-label">
                  <Label id="seed-contact-to-label" htmlFor="seed-contact-to">
                    {t({ id: "settlingIn.days.generate.contactTo", message: "Contact to" })}
                  </Label>
                  <TimePicker
                    id="seed-contact-to"
                    value={seedContactTo}
                    onChange={setSeedContactTo}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step controls: Back/Next on the first two steps, the two finishing
              actions on the last. */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => (step === STEP_WHO ? onOpenChange(false) : setStep((s) => s - 1))}
              disabled={isBusy}
            >
              {step === STEP_WHO
                ? t({ id: "common.cancel", message: "Cancel" })
                : t({ id: "common.back", message: "Back" })}
            </Button>

            <div className="flex flex-wrap items-center gap-2">
              {/* A closed gate always says why — a disabled Next with no
                  explanation reads as a broken dialog. */}
              {step === STEP_BILLING && !placementChosen && (
                <p className="text-sm text-muted-foreground" data-testid="billing-gate-hint">
                  {t({
                    id: "settlingIn.create.billing.gateHint",
                    message: "Select a childcare and group to continue.",
                  })}
                </p>
              )}
              {step === STEP_WHO && (
                <Button
                  type="button"
                  onClick={() => setStep(STEP_BILLING)}
                  disabled={!canLeaveWhoStep || isBusy}
                  data-testid="step-next"
                >
                  {t({ id: "common.next", message: "Next" })}
                </Button>
              )}
              {step === STEP_BILLING && (
                <Button
                  type="button"
                  onClick={() => setStep(STEP_SEED)}
                  disabled={!canLeaveBillingStep || isBusy}
                  data-testid="step-next"
                >
                  {t({ id: "common.next", message: "Next" })}
                </Button>
              )}
              {step === STEP_SEED && (
                <>
                  <Button
                    type="submit"
                    variant="outline"
                    onClick={() => {
                      seedRequestedRef.current = false;
                    }}
                    // The billing snapshot gates confirmation too: if the query
                    // fails after step 2, the plan must not be created against a
                    // configuration nobody ever saw.
                    disabled={isBusy || !billingReady}
                    data-testid="seed-skip"
                  >
                    {t({ id: "settlingIn.create.seed.skip", message: "Skip and create" })}
                  </Button>
                  <Button
                    type="submit"
                    onClick={() => {
                      seedRequestedRef.current = true;
                    }}
                    // Closure dates gate seeding: without them the generator
                    // would happily plan days on holidays and closure days.
                    disabled={isBusy || !closuresReady || !billingReady}
                    data-testid="seed-generate"
                  >
                    {t({ id: "settlingIn.create.seed.submit", message: "Generate & create" })}
                  </Button>
                </>
              )}
            </div>
          </div>
        </BaseEditableForm>
      </DialogContent>
    </Dialog>
  );
}
